import React, { FC } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import queryString from 'query-string'

import theme from '@app/ui/theme'
import { Button } from '@ui/blocks/Button'
import { TextInput } from '@ui/blocks/TextInput'
import { ErrorMessage } from '@ui/blocks/ErrorMessage'
import { getSessionIdSelector } from '@app/store/session'
import { isBrowser } from '@app/utils/isBrowser'
import { segmentEventsNames, trackSegmentEvent, abTestsUtils, facebookPixelEvents } from '@app/utils/analytics'
import { LOCATION_FILTER_AB_TEST_VARIANTS } from '@app/constants/booking'

import {
  actions,
  BookingInputFieldsNames,
  BookingSteps,
  checkIfZipCodeIsSupported,
  inputDataSelector,
  testVariantIdLocationFilterSelector
} from '@app/store/booking'

import { getBookingUrl } from './utils'

export interface ZipCodeTestIds {
  form?: string
  input?: string
  button?: string
}

interface ZipCodeFormProps {
  variant?: 'green' | 'white' | 'deepSea'
  className?: string
  silentError?: boolean
  onSubmit?: (zipCode: string) => void
  testIds?: ZipCodeTestIds
  placement?: string
  loadDefaultFromStore?: boolean
}

interface FormData {
  zipCode: string
}

const VALIDATION_MESSAGE = 'Please type a 5 digit zip code'

const schema = yup.object().shape({
  zipCode: yup
    .string()
    .required(VALIDATION_MESSAGE)
    .matches(/^[0-9]{5}(?:-[0-9]{4})?$/, VALIDATION_MESSAGE)
})

const FormWrapper = styled.div`
  max-width: 36.4rem;

  @media ${theme.device.laptop} {
    max-width: 100%;
  }
`

const StyledForm = styled.form`
  display: flex;

  @media ${theme.device.tablet} {
    max-width: 100%;
    flex-wrap: wrap;
  }
`

const StyledTextInput = styled(TextInput)`
  flex-grow: 1;
  margin-right: 1.1rem;
  min-width: 15rem;
  max-width: 18rem;

  @media ${theme.device.tablet} {
    margin-right: 0;
    margin-bottom: 1.2rem;
    max-width: 100%;
  }
`

export const StyledButton = styled(Button)`
  flex-shrink: 0;
  margin-right: 0.8rem;
  padding: 0.2rem 3rem;

  @media ${theme.device.tablet} {
    width: 100%;
  }
`

export const SilentErrorMessage = styled(ErrorMessage)`
  min-width: 20rem;
  position: static;
  height: 4.4rem;
  display: flex;
  align-items: center;
`

export const DEFAULT_TEST_IDS: ZipCodeTestIds = {
  form: 'zip-code-form',
  input: 'zip-code-input',
  button: 'zip-code-button'
}

const ZipCodeForm: FC<ZipCodeFormProps> = ({
  variant = 'white',
  className,
  silentError,
  onSubmit,
  placement,
  testIds = DEFAULT_TEST_IDS,
  loadDefaultFromStore
}: ZipCodeFormProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const sessionId = useSelector(getSessionIdSelector)
  const inputData = useSelector(inputDataSelector)
  const isLoadDefaultValues = loadDefaultFromStore && inputData.zipCode
  const testVariantIdLocationFilter = useSelector(testVariantIdLocationFilterSelector)
  const url = isBrowser ? decodeURI(window.location.href) : ''
  const parsedURL = queryString.parseUrl(url)

  const { register, handleSubmit, errors } = useForm<FormData>({
    resolver: yupResolver(schema),
    ...(isLoadDefaultValues && {
      defaultValues: {
        zipCode: inputData.zipCode
      }
    })
  })

  const onSubmitHandler = handleSubmit(async (data: FormData) => {
    if (typeof onSubmit === 'function') {
      // Submit fcn passed from Welcome booking screen
      onSubmit(data.zipCode)
    } else {
      // "Native" form's submit
      dispatch(
        actions.successUpdateBookingInputData({
          fieldName: BookingInputFieldsNames.zipCode,
          fieldValue: data.zipCode
        })
      )

      if (abTestsUtils.isZipCodeValidationNeeded(testVariantIdLocationFilter)) {
        dispatch(checkIfZipCodeIsSupported({ zipCode: data.zipCode }))
      }

      if (sessionId) {
        trackSegmentEvent(sessionId, segmentEventsNames.GET_STARTED_CLICK, {
          page: placement
        })
        trackSegmentEvent(sessionId, segmentEventsNames.BOOKING_FLOW_STARTED, {
          utm_medium: parsedURL.query.utm_medium,
          utm_source: parsedURL.query.utm_source,
          campaign: parsedURL.query.campaign,
          ad_group: parsedURL.query.adgroup,
          ad: parsedURL.query.ad,
          location: parsedURL.query.location,
          test_variant: abTestsUtils.isControlVariantLocationFilter(testVariantIdLocationFilter)
            ? LOCATION_FILTER_AB_TEST_VARIANTS.control
            : LOCATION_FILTER_AB_TEST_VARIANTS.test
        })
        trackSegmentEvent(sessionId, segmentEventsNames.BOOKING_FLOW_POSTAL_CODE_COMPLETED, {
          postalCode: data.zipCode
        })
      }

      facebookPixelEvents.bookingStarted()

      // Set 2 step for booking
      dispatch(actions.setBookingStep(BookingSteps.Age))

      const bookingURL = getBookingUrl(data.zipCode)
      history.push(bookingURL)
    }
  })

  return (
    <FormWrapper className={className}>
      <StyledForm onSubmit={onSubmitHandler} className="zip-code-form" data-test-id={testIds.form}>
        <StyledTextInput
          className="zip-code-input"
          variant={variant}
          register={register}
          error={errors.zipCode?.message}
          name="zipCode"
          placeholder="Enter Zip Code"
          autoComplete="off"
          silentError={silentError}
          data-test-id={testIds.input}
        />
        <StyledButton
          data-test-id={testIds.button}
          onClick={onSubmitHandler}
          variant="main"
          className="zip-code-button">
          Get Started
        </StyledButton>
        {silentError && errors.zipCode?.message && <SilentErrorMessage>{errors.zipCode?.message}</SilentErrorMessage>}
      </StyledForm>
    </FormWrapper>
  )
}

export default ZipCodeForm
